/**
 * @file: ConversationHeaderRight.tsx
 *
 * The mobile stack-header `⋮` button. Always routes to the room detail
 * page; when no room exists yet (fresh private chat with no messages) the
 * detail screen renders a phantom state with me + the counterpart and a
 * disabled settings panel, from where `+` opens the contact picker.
 */

import {
  ChatConversationHandle,
  isBaked,
} from '@euler/app/flows/chat/lib/conversation/handle';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { InboxNavParams } from '@euler/app/flows/inbox/screens';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { MoreVertical } from 'lucide-react-native';
import { FC, memo, useCallback, useMemo } from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';
import { ConversationIntent } from '../../lib/conversation/types';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

/** The `⋮` opens the room detail with the conversation's resolved handle. A
 * `contact` intent → a `bare` handle (phantom, no room yet); a `resolved`
 * intent → its handle. A still-pending `room` intent (a push not yet resolved)
 * has no handle, so the button waits until the screen rewrites it to resolved. */
function handleFromIntent(
  intent: ConversationIntent | undefined,
): ChatConversationHandle | undefined {
  if (intent?.kind === 'resolved') return intent.handle;
  if (intent?.kind === 'contact') return { kind: 'bare', peer: intent.peer };
  return undefined;
}

export const ConversationHeaderRight: FC<{
  route: { params?: ConversationIntent };
  style?: StyleProp<ViewStyle>;
}> = memo(({ route, style }) => {
  const { rpx } = useResponsiveContext();
  const navigation = useNavigation<StackNavigationProp<InboxNavParams>>();
  const handle = useMemo(
    () => handleFromIntent(route?.params),
    [route?.params],
  );
  const onPress = useCallback(() => {
    if (handle) navigation.navigate('_chatRoomDetail' as any, handle);
  }, [navigation, handle]);

  // Hide the `⋮` once the room is closed for me (removed / dismissed): the
  // room detail would 404. The conversation body publishes closure to the
  // session; this header lives outside that provider, so it reads the signal.
  const { closedRoomIds$ } = useChatSession();
  const [closedRoomIds] = useBehaviorSubject(closedRoomIds$);
  const roomId = handle && isBaked(handle) ? handle.roomId : null;
  if (roomId && closedRoomIds.has(roomId)) return null;

  return (
    <TouchableOpacity
      onPress={onPress}
      hitSlop={kHitSlop}
      style={style}
      css={`
        padding: 0 ${rpx(12)}px;
      `}
    >
      <MoreVertical size={rpx(20)} color="#111" />
    </TouchableOpacity>
  );
});
